import { faFile, faFolderClosed } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useHistory } from 'react-router';
import {
    useBlockLayout,
    useExpanded,
    useFilters,
    useGlobalFilter,
    useResizeColumns,
    useRowSelect,
    useSortBy,
    useTable
} from 'react-table';
import images from '../../../assets';
import { getCommaSeparatedString } from '../../../CommonUtilities';
import { constantStrings } from '../../../constantStrings';
import { ProfileIconLoader } from '../../styles';
import GenericHeadingText from '../../UIComponents/GenericHeading';
import IBILoader from '../../UIComponents/IBILoader';
import FormDateTimePicker from '../FormElements/FormDateAndTimePicker';
import FormInput from '../FormElements/FormInput';
import FormSelect from '../FormElements/FormSelect';
import { CustomCheckBox } from '../styles';
import { CustomTable } from './styles';
import { TableFilterComponent } from './TableFilterComponent';

const getStickingDirection = (className: string) => {
    if (className === 'sticky-left') return 'left';
    if (className === 'sticky-right') return 'right';
    return null;
};

const TableRowsList = ({
    rows,
    visibleColumns,
    selectedRow,
    setSelectedRow,
    prepareRow,
    redirectMethod,
    renderRowSubComponent,
    columnWidths,
    noDataText
}: any) => {
    if (rows.length === 0)
        return (
            <tr>
                <td colSpan={visibleColumns.length}>
                    <GenericHeadingText title={noDataText} />
                </td>
            </tr>
        );
    return rows.map((row: any, i: any) => {
        prepareRow(row);
        return (
            <>
                <tr
                    {...row.getRowProps()}
                    onClick={(e: any) => {
                        redirectMethod(row, e);
                        setSelectedRow((prevSelectedRow: number | null) =>
                            prevSelectedRow === row.id ? null : row.id
                        );
                    }}
                    className={selectedRow === row.id ? 'selected-row' : ''}
                    onContextMenu={(e) => {
                        redirectMethod(row, e, true);
                    }}
                >
                    {row.cells.map((cell: any) => {
                        const direction = getStickingDirection(cell.column.headerClassname);
                        return (
                            <td
                                {...cell.getCellProps()}
                                title={cell.value}
                                className={cell.column.headerClassname}
                                style={{
                                    width: `${cell.column.width}px`,
                                    ...(direction
                                        ? {
                                              [direction]: `${
                                                  columnWidths[direction][cell.column.id]
                                              }px`
                                          }
                                        : {})
                                }}
                            >
                                {cell.column.isFolder && (
                                    <FontAwesomeIcon icon={faFolderClosed} className='pe-3' />
                                )}
                                {cell.column.isFile && (
                                    <FontAwesomeIcon icon={faFile} className='pe-3' />
                                )}
                                {cell.render('Cell')}
                            </td>
                        );
                    })}
                </tr>
                {row.isExpanded ? (
                    <tr>
                        <td colSpan={visibleColumns.length}>{renderRowSubComponent({ row })}</td>
                    </tr>
                ) : null}
            </>
        );
    });
};

export const EditableCell = ({
    value: initialValue,
    row: { index, original },
    column: {
        id,
        isNonEditableColumn,
        isAmount,
        isDate,
        options,
        autoFocus,
        renderFooterComponent
    },
    updateMyData,
    editableRowIndex,
    isEditable,
    useFormInput,
    isInvoice
}: // This is a custom function that we supplied to our table instance
any) => {
    // We need to keep and update the state of the cell normally
    const [value, setValue] = useState(initialValue);
    const methods = useFormContext();
    const onChange = (e: any) => {
        let value = e.target.value;

        if (isAmount) {
            value = getCommaSeparatedString(e.target.value);
        }
        setValue(value);
    };

    // We'll only update the external data when the input is blurred
    const onBlur = () => {
        updateMyData(index, id, value);
    };

    // If the initialValue is changed external, sync it up with our state
    useEffect(() => {
        setValue(initialValue ?? '');
    }, [initialValue]);

    let returnComponent: any = null;

    if (
        ((index === editableRowIndex && !isNonEditableColumn) ||
            (isEditable && !isNonEditableColumn && !original.isReadOnly)) &&
        !(original?.payment_status === 'Paid' || original?.payment_status === 'Verifying Payment')
    ) {
        if (isInvoice) {
            returnComponent = (
                <input
                    className='bg-transparent'
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    title={value}
                />
            );
        } else if (typeof isDate === 'function' ? isDate(original) : isDate) {
            returnComponent = (
                <FormDateTimePicker
                    name={id}
                    className={''}
                    value={value}
                    placeHolderText={i18n.t(constantStrings.datePlaceholder)}
                    autoFocus={autoFocus}
                />
            );
        } else if (options) {
            returnComponent = (
                <>
                    <FormSelect
                        displayName={''}
                        className={''}
                        name={id}
                        options={typeof options === 'function' ? options(original) : options}
                    />
                </>
            );
        } else {
            returnComponent = !useFormInput ? (
                <>
                    <input
                        type='text'
                        name={id}
                        className=' bg-transparent'
                        value={isAmount ? getCommaSeparatedString(value) : value}
                        onChange={onChange}
                        onBlur={onBlur}
                    />
                </>
            ) : (
                <FormInput name={id} type='text' asterisk={false} autoFocus={!!autoFocus} />
            );
        }
    } else {
        returnComponent = <div>{typeof value === 'object' ? value?.toString() : value}</div>;
    }
    if (renderFooterComponent) {
        const currentValue = methods.watch(id);
        returnComponent = (
            <div className={'d-flex flex-column'}>
                {returnComponent}{' '}
                {renderFooterComponent({
                    row: { original, editableRowIndex, index },
                    currentValue: currentValue?.value
                })}
            </div>
        );
    }
    return returnComponent;
};

// Set our editable cell renderer as the default Cell renderer
const defaultColumn = {
    Cell: EditableCell,
    width: '250'
};

const TableContainer = ({
    columns,
    data,
    getRowId,
    rowClick,
    path,
    history,
    pageSize,
    getNextSetOfData,
    totalCount,
    children,
    isBorderNone,
    checkBox,
    setSelectedRows,
    initialSelectedRows,
    setSelectedRowsData,
    getSelectedRowsData,
    updateMyData,
    actualTableCount,
    isEditable,
    hiddenColumns,
    isInvoice,
    id,
    renderRowSubComponent,
    useFormInput,
    expandedRow,
    disableSort,
    keys,
    filterData,
    setFilterData,
    showHeaderCheckBox = false,
    fixedHeaderCheckboxColumn = false,
    noDataText,
    handleRightClick,
    notificationTable = false,
    truncatedText = true,
    freezeColumns = false
}: any) => {
    const [showOverlay, setShowOverlay] = useState(false);
    const [selectedRow, setSelectedRow] = useState<number | null>(null);
    const [columnHeader, setColumnHeader] = useState('');
    const [editableRowIndex, setEditableRowIndex] = useState(null);
    const [sortState, setSortState] = useState<string>('');
    const [tableData, setTableData] = useState<any>(data);
    const [activeFilter, setActiveFilter] = useState<any>({ ...filterData });
    const [columnWidths, setColumnWidths] = useState<any>({ left: [], right: [] });
    const headerRefs = useRef<any>([]);
    const { t } = useTranslation();
    const [hasMore, setHasMore] = useState(data.length > (pageSize || 10));

    const fetchMoreData = () => {
        if (hasMore) {
            if (notificationTable) return getNextSetOfData();
            return getNextSetOfData && getNextSetOfData(data.length);
        }
    };
    const browserHistory: any = useHistory();

    useEffect(() => {
        if (
            !Object.values(filterData || {}).length &&
            browserHistory?.location?.state?.filters &&
            setFilterData
        ) {
            setFilterData(browserHistory.location.state.filters);
            setActiveFilter(browserHistory.location.state.filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setTableData(data);
        if (notificationTable) setHasMore(false);
        else {
            setHasMore(actualTableCount ? actualTableCount < totalCount : data.length < totalCount);
        }
    }, [actualTableCount, data, totalCount, notificationTable]);

    const memoizedColumns = useMemo(() => {
        const defaultFixedColumns = [
            t(constantStrings.verificationID),
            t(constantStrings.buildingName)
        ];
        return [...columns].map((column: any) => {
            if (freezeColumns) {
                if (defaultFixedColumns.includes(column.Header)) {
                    return {
                        ...column,
                        headerClassname: 'sticky-left'
                    };
                }
            }
            return column;
        });
    }, [columns, freezeColumns, t]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        //@ts-ignore
        selectedFlatRows,
        //@ts-ignore
        state: { selectedRowIds, sortBy },
        visibleColumns,
        //@ts-ignore
        toggleRowExpanded,
        //@ts-ignore
        setSortBy
    } = useTable(
        {
            columns: memoizedColumns,
            defaultColumn,
            data: tableData,
            initialState: {
                //@ts-ignore
                selectedRowIds: initialSelectedRows ? initialSelectedRows : {},
                hiddenColumns: hiddenColumns || []
            },
            getRowId: getRowId || ((row, index) => index),
            updateMyData,
            editableRowIndex,
            setEditableRowIndex,
            isEditable,
            isInvoice,
            useFormInput,
            disableSort,
            autoResetSortBy: false
        },
        // hook for sorting
        useFilters,
        useGlobalFilter,
        useSortBy,
        useExpanded,
        useRowSelect,
        useBlockLayout,
        useResizeColumns,
        (hooks) => {
            checkBox &&
                hooks.visibleColumns.push((columns: any) => [
                    {
                        id: 'selection',
                        width: 60,
                        Header: ({
                            getToggleAllRowsSelectedProps
                        }: {
                            getToggleAllRowsSelectedProps: any;
                        }) => (
                            <div>
                                {showHeaderCheckBox && (
                                    <CustomCheckBox>
                                        <input
                                            type='checkbox'
                                            {...getToggleAllRowsSelectedProps()}
                                        />
                                        <span className='duplicate-checkbox'></span>
                                    </CustomCheckBox>
                                )}
                            </div>
                        ),
                        headerClassname: fixedHeaderCheckboxColumn ? 'sticky-left' : '',
                        Filter: false,
                        Cell: ({ row }: { row: any }) => (
                            <div>
                                <CustomCheckBox>
                                    <input type='checkbox' {...row.getToggleRowSelectedProps()} />
                                    <span className='duplicate-checkbox'></span>
                                </CustomCheckBox>
                            </div>
                        )
                    },
                    ...columns
                ]);
        }
    );

    useEffect(() => {
        const widths: { left: number[]; right: number[] } = { left: [], right: [] };
        headerRefs.current.forEach((ref: any, index: number) => {
            if (ref) {
                const columnName = ref.getAttribute('name');
                if (ref.className.includes('sticky-left')) {
                    const sumPrevStickyWidths = headerRefs.current
                        .slice(0, index)
                        .reduce(
                            (acc: number, prevRef: any) =>
                                prevRef.className.includes('sticky-left')
                                    ? acc + prevRef.offsetWidth
                                    : acc,
                            0
                        );
                    widths.left[columnName] = sumPrevStickyWidths;
                } else if (ref.className.includes('sticky-right')) {
                    const sumNextStickyWidths = headerRefs.current
                        .slice(index + 1)
                        .reduce(
                            (acc: number, nextRef: any) =>
                                nextRef.className.includes('sticky-right')
                                    ? acc + nextRef.offsetWidth
                                    : acc,
                            0
                        );
                    widths.right[columnName] = sumNextStickyWidths;
                }
            }
        });
        setColumnWidths(widths);
    }, [headerGroups]);

    useEffect(() => {
        getSelectedRowsData && setSelectedRows && setSelectedRows(selectedRowIds);
    }, [selectedRowIds, setSelectedRows, getSelectedRowsData]);

    useEffect(() => {
        if (getSelectedRowsData && setSelectedRowsData) {
            setSelectedRowsData(selectedFlatRows);
        }
        // eslint-disable-next-line
    }, [selectedFlatRows.length, setSelectedRowsData]);

    useEffect(() => {
        if (toggleRowExpanded) {
            rows.forEach((row: any) => {
                if (localStorage.getItem('editedMeter')) {
                    const editedMeter = localStorage.getItem('editedMeter');
                    //@ts-ignore
                    if (row.original.id === parseInt(editedMeter)) {
                        toggleRowExpanded(row.id, true);
                        localStorage.removeItem('editedMeter');
                    }
                }
            });
        }
    }, [toggleRowExpanded, expandedRow, rows]);

    const generateSortingIndicator = (column: any) => {
        const { Filter } = column;
        const filterKey = Filter?.filterKey;

        if (Filter?.isLoading) {
            return <ProfileIconLoader className='fas fa-spinner fa-spin fa-lg' />;
        }

        const isFilterActive = !activeFilter[filterKey?.startKey ?? filterKey];

        const sortImage = isFilterActive ? 'FilterImg' : 'Funnel';

        return <img src={images[sortImage]} alt={`${t(constantStrings.sort)}`} />;
    };

    const redirectMethod = (row: any, e: any, rightClick = false) => {
        let payload: any = {};
        if (id) {
            payload = {
                data: row.original,
                id: id
            };
        } else {
            payload = row.original;
        }
        if (rightClick && handleRightClick) handleRightClick(payload);
        else {
            if (rowClick) rowClick(payload);
            if (e.target.localName === 'div' || e.target.localName === 'td') {
                path && history && history.push(path, payload);
            }
        }
    };

    return (
        <CustomTable
            id={'table-responsive' + keys}
            className='table-responsive h-100'
            isBorderNone={isBorderNone}
        >
            <InfiniteScroll
                dataLength={rows.length}
                next={fetchMoreData}
                loader={<IBILoader />}
                hasMore={hasMore}
                scrollableTarget={'table-responsive' + keys}
                style={{ overflow: 'inherit' }}
                onScroll={() => setShowOverlay(false)}
            >
                <table {...getTableProps()}>
                    {children}

                    <thead>
                        {headerGroups.map((headerGroup: any) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column: any, index: number) => {
                                    // addition to column: isSorted, isSortedDesc, getSortByToggleProps
                                    const {
                                        render,
                                        getHeaderProps,
                                        isSorted,
                                        isSortedDesc,
                                        getSortByToggleProps,
                                        showFilter = false,
                                        id,
                                        Filter,
                                        headerClassname = ''
                                    } = column;
                                    const extraClass = isSorted
                                        ? isSortedDesc
                                            ? 'desc'
                                            : 'asc'
                                        : '';
                                    const direction = getStickingDirection(column.headerClassname);
                                    if (!disableSort) {
                                        return (
                                            <th
                                                className={`${extraClass} ${headerClassname}`}
                                                // getHeaderProps now receives a function
                                                {...getHeaderProps(
                                                    getSortByToggleProps({
                                                        title: t(constantStrings.doubleClickToSort)
                                                    })
                                                )}
                                                ref={(el) => (headerRefs.current[index] = el)}
                                                style={{
                                                    width: `${column.width}px`,
                                                    ...(direction
                                                        ? {
                                                              [direction]: `${
                                                                  columnWidths[direction][column.id]
                                                              }px`
                                                          }
                                                        : {})
                                                }}
                                                name={column.id}
                                                onClick={(e) => e.stopPropagation()}
                                                onDoubleClick={() => {
                                                    column.toggleSortBy(!column.isSortedDesc);
                                                }}
                                            >
                                                <div className='d-flex align-items-center justify-content-between'>
                                                    <b title={column.Header}>{render('Header')}</b>
                                                    {showFilter && (
                                                        <div
                                                            data-tip
                                                            data-for={'ps-2 filter'}
                                                            data-event={'click'}
                                                        >
                                                            {generateSortingIndicator(column)}
                                                        </div>
                                                    )}
                                                    {!column.headerClassname && (
                                                        <div
                                                            {...column.getResizerProps()}
                                                            className={`resizer ${
                                                                column.isResizing
                                                                    ? 'isResizing'
                                                                    : ''
                                                            }`}
                                                        />
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    } else {
                                        return (
                                            <th
                                                className={`${extraClass} ${headerClassname} d-flex`}
                                                // getHeaderProps now receives a function
                                                {...getHeaderProps({ title: column.Header })}
                                                ref={(el) => (headerRefs.current[index] = el)}
                                                style={{
                                                    width: `${column.width}px`,
                                                    ...(direction
                                                        ? {
                                                              [direction]: `${
                                                                  columnWidths[direction][column.id]
                                                              }px`
                                                          }
                                                        : {})
                                                }}
                                                name={column.id}
                                                onClick={() =>
                                                    Filter &&
                                                    !column.Filter?.isLoading &&
                                                    setColumnHeader(column.Header)
                                                }
                                            >
                                                <div className='d-flex align-items-center justify-content-between flex-grow-1'>
                                                    <b>{render('Header')}</b>
                                                    {Filter && (
                                                        <>
                                                            <OverlayTrigger
                                                                key={column.Header}
                                                                trigger='click'
                                                                placement='bottom'
                                                                rootClose
                                                                show={
                                                                    columnHeader ===
                                                                        column.Header && showOverlay
                                                                }
                                                                overlay={
                                                                    <Popover>
                                                                        <Popover.Body className='p-0'>
                                                                            <TableFilterComponent
                                                                                id={id}
                                                                                filterValues={
                                                                                    Filter
                                                                                }
                                                                                toggleSortBy={
                                                                                    column.toggleSortBy
                                                                                }
                                                                                sortBy={sortBy}
                                                                                setSortBy={
                                                                                    setSortBy
                                                                                }
                                                                                setShowOverlay={
                                                                                    setShowOverlay
                                                                                }
                                                                                sortState={
                                                                                    sortState
                                                                                }
                                                                                setSortState={
                                                                                    setSortState
                                                                                }
                                                                                activeFilter={
                                                                                    activeFilter
                                                                                }
                                                                                setActiveFilter={
                                                                                    setActiveFilter
                                                                                }
                                                                                filterData={
                                                                                    filterData
                                                                                }
                                                                                setFilterData={
                                                                                    setFilterData
                                                                                }
                                                                            />
                                                                        </Popover.Body>
                                                                    </Popover>
                                                                }
                                                                onToggle={() =>
                                                                    setShowOverlay(
                                                                        columnHeader !==
                                                                            column.Header
                                                                    )
                                                                }
                                                            >
                                                                <div
                                                                    onClick={() =>
                                                                        setShowOverlay(true)
                                                                    }
                                                                >
                                                                    {generateSortingIndicator(
                                                                        column
                                                                    )}
                                                                </div>
                                                            </OverlayTrigger>
                                                        </>
                                                    )}
                                                    {!column.headerClassname && (
                                                        <div
                                                            {...column.getResizerProps()}
                                                            className={`resizer ${
                                                                column.isResizing
                                                                    ? 'isResizing'
                                                                    : ''
                                                            }`}
                                                        />
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    }
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        <TableRowsList
                            columnWidths={columnWidths}
                            rows={rows}
                            selectedRow={selectedRow}
                            setSelectedRow={setSelectedRow}
                            visibleColumns={visibleColumns}
                            noDataText={noDataText || t(constantStrings.noDataFound)}
                            prepareRow={prepareRow}
                            redirectMethod={redirectMethod}
                            renderRowSubComponent={renderRowSubComponent}
                        />
                    </tbody>
                </table>
            </InfiniteScroll>
        </CustomTable>
    );
};

export default TableContainer;
